import { useParams, useNavigate } from 'react-router-dom';
import { Edit, Folder } from 'lucide-react';
import { Button } from '@/components/Button';
import { DetailHeader } from '@/components/DetailHeader';
import { DeleteButton } from '@/components/DeleteButton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/Tabs';
import { useCollection, useDeleteCollection } from '@/features/collections/hooks';
import { CollectionModelBrowser, CollectionChildrenTree } from '@/features/collections/components';
import { useState } from 'react';
import { useUIStore } from '@/stores/ui';

export function CollectionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('models');

  const { data: collection, isLoading } = useCollection(id!);
  const deleteCollection = useDeleteCollection();
  const { addToast, openCollectionSidebar } = useUIStore();

  const handleDelete = async () => {
    if (!collection) return;

    if (!confirm(`Are you sure you want to delete "${collection.name}"?`)) {
      return;
    }

    setDeletingId(collection.id);
    try {
      await deleteCollection.mutateAsync(collection.id);
      addToast({
        type: 'success',
        title: 'Collection deleted',
        message: `"${collection.name}" has been deleted`,
      });
      navigate('/collections');
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Failed to delete collection',
        message: error instanceof Error ? error.message : 'An error occurred',
      });
    } finally {
      setDeletingId(null);
    }
  };

  if (isLoading || !collection) {
    return (
      <div className="p-4">
        <div className="rounded border border-border bg-background-subtle p-4">
          <div className="text-center text-foreground-muted">Loading collection...</div>
        </div>
      </div>
    );
  }

  const isDeleting = deletingId === collection.id;

  // Calculate stats
  const directItems = collection.modelCount || 0;
  const childCount = collection.children?.length || 0;

  // Build metadata for header
  const metadata = (
    <div className="flex items-center gap-3">
      <span className="text-xs text-foreground-muted">
        {directItems} {directItems === 1 ? 'Model' : 'Models'}
      </span>
      {childCount > 0 && (
        <>
          <span className="text-xs text-foreground-muted">•</span>
          <span className="text-xs text-foreground-muted">
            {childCount} Sub-collection{childCount === 1 ? '' : 's'}
          </span>
        </>
      )}
    </div>
  );

  // Build actions for header
  const actions = (
    <>
      <Button
        size="sm"
        variant="secondary"
        leftIcon={<Edit className="h-4 w-4" />}
        onClick={() => openCollectionSidebar({ collection })}
      >
        Edit
      </Button>
      <DeleteButton
        onClick={handleDelete}
        disabled={isDeleting}
      />
    </>
  );

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <DetailHeader
        onBack={() => navigate('/collections')}
        backLabel="Collections"
        icon={Folder}
        iconSize={12}
        title={collection.name}
        description={collection.description}
        metadata={metadata}
        actions={actions}
      />

      {/* Tabs */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="px-4 border-b border-border">
            <TabsList>
              <TabsTrigger value="models">
                Models {directItems > 0 && `(${directItems})`}
              </TabsTrigger>
              <TabsTrigger value="collections">
                Sub-collections {childCount > 0 && `(${childCount})`}
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="flex-1 overflow-y-auto">
            <TabsContent value="models" className="p-4">
              <CollectionModelBrowser collectionId={collection.id} />
            </TabsContent>

            <TabsContent value="collections" className="p-4">
              <CollectionChildrenTree collection={collection} />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
}
